'use client';

import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchNewsStats, type NewsStatsItem } from '@/utils/api/news';

/**
 * Свежие счётчики (views/comments/likes) для набора news-документов (P11).
 *
 * Архитектурный паттерн: «долгий кэш для тяжёлых данных + точечный refresh
 * для счётчиков». Этот хук — точечная часть:
 *   - Каждые 5 секунд react-query считает stale → refetch (если хук активен).
 *   - При act-событиях (view-tracking, like, comment) вызывающий код делает
 *     queryClient.invalidateQueries({ queryKey: ['news-stats', documentId] }).
 *
 * Возвращает Map<documentId, NewsStatsItem> для лёгкого lookup'а в карточке.
 *
 * Семантика fallback: если в map нет записи (или хук ещё не загрузил) — рендер
 * берёт baseline из feed/snapshot DTO. Это не показывает «свежее значение»,
 * но не показывает и нулей.
 *
 * NOTE: хук всегда вызывается с тем же набором docIds в рамках одной страницы.
 * Кэш делится между всеми вызывающими компонентами через стабильный queryKey.
 *
 * Backend: `GET /api/bff/news/feed/stats?docIds=<csv>` без Redis-кэша.
 */
export function useNewsStats(docIds: string[]): {
  statsMap: Map<string, NewsStatsItem>;
  isLoading: boolean;
} {
  const queryClient = useQueryClient();

  // Стабилизируем ключ: одинаковый набор docIds в разном порядке = тот же ключ.
  const normalizedIds = useMemo(() => {
    if (docIds.length === 0) return [];
    return Array.from(new Set(docIds)).sort();
  }, [docIds]);

  const queryKey = useMemo(() => ['news-stats', ...normalizedIds], [normalizedIds]);

  const query = useQuery({
    queryKey,
    queryFn: () => fetchNewsStats(normalizedIds),
    enabled: normalizedIds.length > 0,
    // 5 секунд — компромис: после view/like/comment мы инвалидируем queryKey,
    // так что свежие значения приходят моментально. staleTime гасит лишние
    // запросы при ре-рендерах в течение коротких интервалов.
    staleTime: 5_000,
    // Не запрашиваем фоном — обновление только по invalidate или re-mount.
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });

  const statsMap = useMemo(() => {
    const m = new Map<string, NewsStatsItem>();
    if (query.data) {
      for (const item of query.data.items) {
        m.set(item.documentId, item);
      }
    }
    return m;
  }, [query.data]);

  // Также экспортируем helper для invalidate'а через queryClient — но проще
  // тащить queryClient.invalidateQueries({ queryKey: ['news-stats'] }) напрямую.
  // Этот voidUse — placeholder для будущего bump-помощника. Пока неиспользуется.
  void queryClient;

  return {
    statsMap,
    isLoading: query.isLoading,
  };
}

/**
 * Утилита: инвалидация всех news-stats query'ев. Вызывается после view/like/comment
 * чтобы карточки получили свежие счётчики.
 *
 * Пример:
 *   const queryClient = useQueryClient();
 *   await setNewsReaction(projectId, documentId, 'like');
 *   invalidateNewsStats(queryClient);
 */
export function invalidateNewsStats(
  queryClient: ReturnType<typeof useQueryClient>,
): void {
  // Инвалидируем ВСЕ news-stats query'и (любые наборы docIds). Это безопасно —
  // refetch сделают только активные хуки (mounted компоненты).
  void queryClient.invalidateQueries({ queryKey: ['news-stats'] });
}
